<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { FileText, Download, Clock, ChevronLeft, ChevronRight, Filter, CheckCircle, XCircle, Clock as ClockIcon } from 'lucide-vue-next'
import { toast } from '@/utils/toast'
import { templateApi } from '@/services/api'
import { useUIStore } from '@/stores/ui'
import type { Template, GenerationRun } from '@/types'

interface Props {
  activeTemplate: Template | null
  allTemplates: Template[]
  baseToken: string
  tableId: string
}

const props = defineProps<Props>()

const { t } = useI18n()
const uiStore = useUIStore()

// State
const generations = ref<GenerationRun[]>([])
const currentPage = ref(1)
const pageSize = ref(20)
const totalPages = ref(0)
const totalCount = ref(0)
const selectedTemplateFilter = ref<string | null>(null)

// Computed
const hasGenerations = computed(() => generations.value.length > 0)
const startIndex = computed(() => (currentPage.value - 1) * pageSize.value + 1)
const endIndex = computed(() => Math.min(currentPage.value * pageSize.value, totalCount.value))

// Format relative time
const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now()
  const diffMs = now - timestamp
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return t('justNow')
  if (diffMins < 60) return t('minutesAgo', { count: diffMins })
  if (diffHours < 24) return t('hoursAgo', { count: diffHours })
  if (diffDays < 7) return t('daysAgo', { count: diffDays })

  // Format as date
  return new Date(timestamp).toLocaleDateString()
}

// Check if generation is expired (deleted)
const isExpired = (generation: GenerationRun): boolean => {
  return generation.deleted_at !== null && generation.deleted_at !== undefined
}

// Format status
const getStatusIcon = (status: string, expired: boolean) => {
  if (expired) {
    return { component: Clock, class: 'text-gray-400' }
  }
  switch (status) {
    case 'completed':
      return { component: CheckCircle, class: 'text-green-600' }
    case 'failed':
      return { component: XCircle, class: 'text-red-600' }
    case 'pending':
      return { component: ClockIcon, class: 'text-yellow-600' }
    default:
      return { component: ClockIcon, class: 'text-gray-600' }
  }
}

const getStatusText = (status: string, expired: boolean): string => {
  if (expired) {
    return t('expired')
  }
  switch (status) {
    case 'completed':
      return t('completed')
    case 'failed':
      return t('failed')
    case 'pending':
      return t('pending')
    default:
      return status
  }
}

// Get template name by ID
const getTemplateName = (templateId: string): string => {
  const template = props.allTemplates.find(t => t.id === templateId)
  return template?.name || templateId
}

// Load generations
const loadGenerations = async () => {
  if (!props.baseToken || !props.tableId) return

  try {
    uiStore.startLoading()
    const result = await templateApi.getGenerations(
      props.baseToken,
      props.tableId,
      currentPage.value,
      pageSize.value,
      selectedTemplateFilter.value || undefined
    )

    generations.value = result.data
    totalPages.value = result.pagination.totalPages
    totalCount.value = result.pagination.total
  } catch (error) {
    console.error('Failed to load generations:', error)
    toast.error(error instanceof Error ? error.message : t('failedToLoadGenerations'))
    generations.value = []
  } finally {
    uiStore.stopLoading()
  }
}

// Download generation using the signed URL from the response
const handleDownload = async (generation: GenerationRun & { downloadUrl?: string; downloadUrlExpiresAt?: number }) => {
  try {
    // Check if the signed URL is provided
    if (!generation.downloadUrl || !generation.downloadUrlExpiresAt) {
      toast.error(t('downloadLinkNotAvailable'))
      await loadGenerations()
      return
    }

    // Check if the signed URL is expired
    if (Date.now() > generation.downloadUrlExpiresAt) {
      toast.error(t('downloadLinkExpired'))
      await loadGenerations()
      return
    }

    // Download directly using the signed URL
    const response = await fetch(generation.downloadUrl)

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`)
    }

    // Create blob and trigger download
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = generation.output_filename || 'generated-document.docx'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)

    toast.success(t('downloadStarted'))
  } catch (error) {
    console.error('Failed to download generation:', error)
    toast.error(error instanceof Error ? error.message : t('failedToDownload'))
  }
}

// Pagination handlers
const goToPage = (page: number) => {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  loadGenerations()
}

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
    loadGenerations()
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    loadGenerations()
  }
}

// Filter by template
const filterByTemplate = (templateId: string | null) => {
  selectedTemplateFilter.value = templateId
  currentPage.value = 1 // Reset to first page
  loadGenerations()
}

// Watch for template/table changes
watch(() => [props.baseToken, props.tableId], () => {
  currentPage.value = 1
  selectedTemplateFilter.value = null
  loadGenerations()
}, { immediate: true })
</script>

<template>
  <div class="flex flex-col h-full bg-gray-50">
    <!-- Header -->
    <div class="bg-white border-b border-gray-200 px-8 py-6">
      <div class="max-w-6xl mx-auto">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Clock :size="28" class="text-blue-600" />
              {{ t('generations') }}
            </h1>
            <p class="text-sm text-gray-600 mt-1">
              {{ t('generationsDescription') }}
            </p>
          </div>

          <!-- Template Filter -->
          <div class="flex items-center gap-3">
            <Filter :size="18" class="text-gray-600" />
            <select
              v-model="selectedTemplateFilter"
              @change="filterByTemplate(selectedTemplateFilter)"
              class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option :value="null">{{ t('allTemplates') }}</option>
              <option
                v-for="template in allTemplates"
                :key="template.id"
                :value="template.id"
              >
                {{ template.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-auto px-8 py-6">
      <div class="max-w-6xl mx-auto">
        <!-- Empty State -->
        <div v-if="!hasGenerations" class="text-center py-16">
          <Clock :size="64" class="mx-auto text-gray-300 mb-4" />
          <p class="text-lg text-gray-500">{{ t('noGenerations') }}</p>
          <p class="text-sm text-gray-400 mt-2">{{ t('noGenerationsHint') }}</p>
        </div>

        <!-- Generations Table - Horizontal Scroll -->
        <div v-else class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <!-- Table Header -->
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="px-6 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">
                    {{ t('filename') }}
                  </th>
                  <th scope="col" class="px-6 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">
                    {{ t('template') }}
                  </th>
                  <th scope="col" class="px-6 py-4 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                    {{ t('status') }}
                  </th>
                  <th scope="col" class="px-6 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">
                    {{ t('createdAt') }}
                  </th>
                  <th scope="col" class="px-6 py-4 text-right text-sm font-semibold text-gray-700 whitespace-nowrap">
                    {{ t('actions') }}
                  </th>
                </tr>
              </thead>

              <!-- Table Body -->
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr
                  v-for="generation in generations"
                  :key="generation.id"
                  :class="[
                    'transition-colors',
                    isExpired(generation) ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'
                  ]"
                >
                  <!-- Filename -->
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-3">
                      <FileText
                        :size="18"
                        :class="isExpired(generation) ? 'text-gray-400' : 'text-blue-600'"
                        class="flex-shrink-0"
                      />
                      <span
                        :class="isExpired(generation) ? 'text-gray-500' : 'text-gray-900'"
                        class="text-sm font-medium"
                        :title="generation.output_filename || generation.id"
                      >
                        {{ generation.output_filename || generation.id }}
                      </span>
                    </div>
                  </td>

                  <!-- Template -->
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      :class="isExpired(generation) ? 'text-gray-400' : 'text-gray-700'"
                      class="text-sm"
                      :title="getTemplateName(generation.template_id)"
                    >
                      {{ getTemplateName(generation.template_id) }}
                    </span>
                  </td>

                  <!-- Status Icon -->
                  <td class="px-6 py-4 whitespace-nowrap text-center">
                    <component
                      :is="getStatusIcon(generation.status, isExpired(generation)).component"
                      :size="20"
                      :class="getStatusIcon(generation.status, isExpired(generation)).class"
                      :title="getStatusText(generation.status, isExpired(generation))"
                    />
                  </td>

                  <!-- Created At -->
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      :class="isExpired(generation) ? 'text-gray-400' : 'text-gray-600'"
                      class="text-sm"
                      :title="new Date(generation.created_at).toLocaleString()"
                    >
                      {{ formatRelativeTime(generation.created_at) }}
                    </span>
                  </td>

                  <!-- Actions -->
                  <td class="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      v-if="generation.status === 'completed' && !isExpired(generation)"
                      @click="handleDownload(generation)"
                      class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Download :size="16" />
                      {{ t('download') }}
                    </button>
                    <span v-else-if="isExpired(generation)" class="text-sm text-gray-400 italic">
                      {{ t('expired') }}
                    </span>
                    <span v-else class="text-sm text-gray-400">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="hasGenerations && totalPages > 1" class="mt-6 flex items-center justify-between">
          <!-- Results Info -->
          <div class="text-sm text-gray-600">
            {{ t('showingResults', { start: startIndex, end: endIndex, total: totalCount }) }}
          </div>

          <!-- Page Controls -->
          <div class="flex items-center gap-2">
            <button
              @click="previousPage"
              :disabled="currentPage === 1"
              :class="[
                'p-2 rounded-lg border transition-colors',
                currentPage === 1
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              ]"
            >
              <ChevronLeft :size="18" />
            </button>

            <!-- Page Numbers -->
            <div class="flex items-center gap-1">
              <button
                v-for="page in totalPages"
                :key="page"
                v-show="page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2"
                @click="goToPage(page)"
                :class="[
                  'min-w-[40px] h-10 px-3 rounded-lg text-sm font-medium transition-colors',
                  page === currentPage
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                ]"
              >
                {{ page }}
              </button>
            </div>

            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              :class="[
                'p-2 rounded-lg border transition-colors',
                currentPage === totalPages
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              ]"
            >
              <ChevronRight :size="18" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
